import { DefaultButton, DialogFooter, Dropdown, IDropdownOption, Label, Modal, Stack, Toggle } from '@fluentui/react';
import { FC, FCWithChildren } from 'react';
import roleApi from '../../redux/apis/roleApi';
import { getEnvSettings } from '../../app/hostBasedSettings';
import { nameof } from '../../utils/stringUtils';
import InlineControl from './InlineControl';
import NumberSpinButton from '../teams/NumberSpinButton'
import { InlineDiv, ModalHeader, Separator } from '../../shared/styled-components';
import { SettingsHookType } from '../../providers/SettingsProvider';
import ThemeProvider from '../../providers/ThemeProvider';

const { environment } = getEnvSettings();

const StyledStack: FCWithChildren<{ label: string }> = ({ children, label }) => (
    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={{ root: { marginBottom: '0.25rem' } }}>
        <Label>{label}</Label>
        {children}
    </Stack>
);

const dayOfWeekOptions: IDropdownOption[] = [
    { key: 1, text: 'Monday' },
    { key: 2, text: 'Tuesday' },
    { key: 3, text: 'Wednesday' },
    { key: 4, text: 'Thursday' },
    { key: 5, text: 'Friday' },
    { key: 6, text: 'Saturday' },
    { key: 0, text: 'Sunday' },
];

const dateOptions: IDropdownOption[] = [
    { key: 'default', text: 'Default' },
    { key: 'iso', text: '2000-12-31' },
    { key: 'short', text: '12/31/2000' },
    { key: 'long', text: 'Dec 31, 2000' },
];

const timeOptions: IDropdownOption[] = [
    { key: '24hour', text: '17:45' },
    { key: '12hour', text: '5:45 PM' },
];

const timeSpanOptions: IDropdownOption[] = [
    { key: 'normal', text: '01:23:45' },
    { key: 'written', text: '1h 23m 45s' },
];

interface IProps extends SettingsHookType {
    isOpen: boolean;
    onDismiss(): void;
}

// ThemeProvider is required because SettingsContext (and SettingsModal) is outside of theme contexts
const SettingsModal: FC<IProps> = (props) => {
    const {
        isOpen,
        onDismiss,
        settings,
        setFirstDayOfWeekFormat,
        setIsDarkModeEnabled,
        setDateFormat,
        setTimeFormat,
        setTimeSpanFormat,
        setPermissionOverride,
        setNotificationDurationSec,
    } = props;

    const { data: myRole } = roleApi.endpoints.getMyRole.useQueryState();
    const canAccessOverrideWithRole = !!myRole && myRole?.id === -1;

    return (
        <ThemeProvider>
            <Modal isOpen={isOpen} styles={{ main: { width: 400 } }}>
                <ModalHeader title="Edit local settings" />
                <StyledStack label="Dark mode">
                    <Toggle id="settings-darkMode"
                        checked={settings.isDarkModeEnabled}
                        onChange={(_, checked) => setIsDarkModeEnabled(checked ?? false)}
                    />
                </StyledStack>
                <StyledStack label="Notification duration (seconds)">
                    <NumberSpinButton
                        value={settings.notificationDurationSec}
                        onChange={setNotificationDurationSec}
                        width={65}
                        min={1}
                    />
                </StyledStack>
                <br />
                <StyledStack label="First day of week">
                    <Dropdown
                        selectedKey={settings.firstDayOfWeek}
                        options={dayOfWeekOptions}
                        onChange={(_, opt) => opt ? setFirstDayOfWeekFormat(opt.key as number) : 1}
                        styles={{ root: { width: 125 } }}
                    />
                </StyledStack>
                <StyledStack label="Date format">
                    <Dropdown
                        selectedKey={settings.dateFormat}
                        options={dateOptions}
                        onChange={(_, opt) => opt ? setDateFormat(opt.key as string) : '24hour'}
                        styles={{ root: { width: 125 } }}
                    />
                </StyledStack>
                <StyledStack label="Time format">
                    <Dropdown
                        selectedKey={settings.timeFormat}
                        options={timeOptions}
                        onChange={(_, opt) => opt ? setTimeFormat(opt.key as string) : '24hour'}
                        styles={{ root: { width: 125 } }}
                    />
                </StyledStack>
                <StyledStack label="Elapsed time format">
                    <Dropdown
                        selectedKey={settings.timeSpanFormat}
                        options={timeSpanOptions}
                        onChange={(_, opt) => opt ? setTimeSpanFormat(opt.key as string) : 'normal'}
                        styles={{ root: { width: 125 } }}
                    />
                </StyledStack>
                {
                    ((environment === 'dev' || environment === 'qa') && canAccessOverrideWithRole) &&
                    <>
                        <br />
                        <Separator>Developer settings</Separator>
                        <InlineControl label="Override role permissions">
                            <InlineDiv gap="0.5rem">
                                <Toggle
                                    checked={settings.permissionOverrides.enabled}
                                    onChange={(_, checked) => setPermissionOverride({ ...settings.permissionOverrides, enabled: checked ?? false })}
                                />
                            </InlineDiv>
                        </InlineControl>
                   
                    </>
                }
                <DialogFooter>
                    <DefaultButton onClick={onDismiss} text="Close" />
                </DialogFooter>
            </Modal>
        </ThemeProvider>
    );
};

SettingsModal.displayName = nameof({ SettingsModal });
export default SettingsModal;